import React, { useState, useEffect } from 'react';
import { formatInTenantTz } from '../../../lib/datetime';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../ui/alert-dialog';
import { Progress } from '../../ui/progress';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  BookOpen,
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Tag,
  TrendingUp,
  Star,
  Upload,
  Download,
  FileText,
  Save,
  Brain,
  BarChart3,
  Target,
  Clock,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';

const KnowledgeBase = () => {
  const { currentBusiness } = useBusiness();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  const [newEntry, setNewEntry] = useState({
    title: '',
    content: '',
    category: 'general',
    tags: '',
    resolution_steps: ''
  });

  const [editEntry, setEditEntry] = useState({
    id: '',
    title: '',
    content: '',
    category: 'general',
    tags: '',
    resolution_steps: ''
  });

  const categories = [
    'general', 'billing', 'technical', 'account', 'product', 'shipping'
  ];

  // D4 — canonical Firebase-authenticated request (fresh ID token + backend base + 401 refresh).
  const { makeAuthenticatedRequest } = useCRMAuth();

  useEffect(() => {
    const fetchEntries = async () => {
      if (!currentBusiness?.brandId) return;
      
      setLoading(true);
      try {
        const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/knowledge-base/${currentBusiness.brandId}`);
        if (response.ok) {
          const data = await response.json();
          setEntries(data.knowledge_base || []);
        } else {
          // D4 — no mock fallback: surface a truthful failure instead of fabricating KB entries.
          throw new Error(`Failed to load knowledge base (HTTP ${response.status})`);
        }
      } catch (error) {
        console.error('Error fetching knowledge base:', error);
        toast({
          title: t('common.error'),
          description: t('ak.failedLoadKb'),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEntries();
  }, [currentBusiness?.brandId]);

  const handleSaveEntry = async () => {
    if (!newEntry.title || !newEntry.content) {
      toast({
        title: t('ak.missingInformation'),
        description: t('ak.fillTitleContent'),
        variant: "destructive",
      });
      return;
    }

    const entry = {
      ...newEntry,
      id: `kb-${Date.now()}`,
      business_id: currentBusiness?.brandId || 'default',
      tags: newEntry.tags.split(',').map(t => t.trim()).filter(t => t),
      resolution_steps: newEntry.resolution_steps.split('\n').map(s => s.trim()).filter(s => s),
      success_rate: 0,
      usage_count: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    try {
      const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/knowledge-base`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry)
      });

      if (response.ok) {
        setEntries(prev => [...prev, entry]);
        setNewEntry({
          title: '',
          content: '',
          category: 'general',
          tags: '',
          resolution_steps: ''
        });

        toast({
          title: t('ak.entryCreated'),
          description: t('ak.entryCreatedDesc'),
        });
      } else {
        throw new Error('Failed to create entry');
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('ak.failedCreateEntry'),
        variant: "destructive",
      });
    }
  };

  const handleEditEntry = async () => {
    if (!editEntry.title || !editEntry.content) {
      toast({
        title: t('ak.missingInformation'),
        description: t('ak.fillTitleContent'),
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/knowledge-base/${editEntry.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editEntry,
          tags: editEntry.tags.split(',').map(t => t.trim()).filter(t => t),
          resolution_steps: editEntry.resolution_steps.split('\n').map(s => s.trim()).filter(s => s),
          updated_at: new Date().toISOString()
        })
      });

      if (response.ok) {
        setEntries(prev => prev.map(entry => 
          entry.id === editEntry.id 
            ? { 
                ...editEntry, 
                tags: editEntry.tags.split(',').map(t => t.trim()).filter(t => t),
                resolution_steps: editEntry.resolution_steps.split('\n').map(s => s.trim()).filter(s => s),
                updated_at: new Date().toISOString()
              }
            : entry
        ));
        
        setEditDialogOpen(false);
        setSelectedEntry(null);
        
        toast({
          title: t('ak.entryUpdated'),
          description: t('ak.entryUpdatedDesc'),
        });
      } else {
        throw new Error('Failed to update entry');
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('ak.failedUpdateEntry'),
        variant: "destructive",
      });
    }
  };

  const openEditDialog = (entry) => {
    setEditEntry({
      id: entry.id,
      title: entry.title,
      content: entry.content,
      category: entry.category,
      tags: Array.isArray(entry.tags) ? entry.tags.join(', ') : entry.tags,
      resolution_steps: Array.isArray(entry.resolution_steps) ? entry.resolution_steps.join('\n') : entry.resolution_steps
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (entry) => {
    setSelectedEntry(entry);
    setDeleteDialogOpen(true);
  };

  const deleteEntry = async () => {
    if (!selectedEntry) return;

    try {
      const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/knowledge-base/${selectedEntry.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setEntries(prev => prev.filter(entry => entry.id !== selectedEntry.id));
        setDeleteDialogOpen(false);
        setSelectedEntry(null);
        
        toast({
          title: t('ak.entryDeleted'),
          description: t('ak.entryDeletedDesc'),
        });
      } else {
        throw new Error('Failed to delete entry');
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('ak.failedDeleteEntry'),
        variant: "destructive",
      });
    }
  };

  const handleViewAnalytics = () => {
    setAnalyticsDialogOpen(true);
  };

  // Real client-side export of the knowledge base already loaded into state (no backend needed).
  const handleExport = () => {
    if (!entries || entries.length === 0) {
      toast({
        title: t('ak.nothingToExport'),
        description: t('ak.nothingToExportDesc'),
      });
      return;
    }

    const blob = new Blob([JSON.stringify(entries, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `knowledge-base-${currentBusiness?.brandId || 'export'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: t('ak.exportComplete'),
      description: t('ak.kbExportedDesc'),
    });
  };

  // No knowledge-base import endpoint exists yet — do not fake a successful import.
  // real wiring lands in the AI backend phase
  const handleImport = () => {
    toast({
      title: t('ak.notAvailableYet'),
      description: t('ak.importUnavailableDesc'),
    });
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (Array.isArray(entry.tags) ? entry.tags : []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || entry.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-gradient-to-br from-white via-blue-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('ak.loadingKb')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-blue-50 to-purple-50">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('ak.kbManagementTitle')}</h1>
            <p className="text-gray-600">{t('ak.kbManagementSubtitle')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <Badge className="bg-green-100 text-green-800">
              <FileText className="h-3 w-3 mr-1 inline" />
              {t('ak.entriesBadge', { count: entries.length })}
            </Badge>
            <Button variant="outline" onClick={handleImport}>
              <Upload className="h-4 w-4 mr-2" />
              {t('ak.import')}
            </Button>
            <Button variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              {t('ak.export')}
            </Button>
          </div>
        </div>

        {/* Knowledge Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('ak.totalEntries')}</p>
                  <p className="text-2xl font-bold text-blue-600">{entries.length}</p>
                </div>
                <BookOpen className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('ak.avgSuccessRate')}</p>
                  <p className="text-2xl font-bold text-green-600">
                    {entries.length > 0 ? (entries.reduce((sum, e) => sum + e.success_rate, 0) / entries.length).toFixed(1) : 0}%
                  </p>
                </div>
                <Star className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('ak.totalUsage')}</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {entries.reduce((sum, e) => sum + e.usage_count, 0)}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('ak.categories')}</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {new Set(entries.map(e => e.category)).size}
                  </p>
                </div>
                <Tag className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs defaultValue="browse" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="browse">{t('ak.tab.browseEntries')}</TabsTrigger>
          <TabsTrigger value="create">{t('ak.tab.createEntry')}</TabsTrigger>
          <TabsTrigger value="analytics">{t('ak.tab.analytics')}</TabsTrigger>
        </TabsList>

        {/* Browse Entries Tab */}
        <TabsContent value="browse" className="space-y-6">
          <div className="flex space-x-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder={t('ak.searchKbPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('ak.allCategories')}</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>
                    {t('ak.cat.' + category)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            {filteredEntries.map((entry) => (
              <Card key={entry.id} className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-900">{entry.title}</h3>
                        <Badge variant="outline">{entry.category}</Badge>
                        <div className="flex items-center space-x-1">
                          <Star className="h-4 w-4 text-yellow-500 fill-current" />
                          <span className="text-sm text-gray-600">{entry.success_rate}%</span>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-3">{entry.content}</p>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {(Array.isArray(entry.tags) ? entry.tags : []).map((tag, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />
                        {t('ak.usedTimesUpdated', { count: entry.usage_count, date: formatInTenantTz(entry.updated_at, { dateStyle: 'medium' }) })}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button size="sm" variant="outline" onClick={() => openEditDialog(entry)}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600"
                            onClick={() => openDeleteDialog(entry)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </AlertDialogTrigger>
                      </AlertDialog>
                    </div>
                  </div>
                  {Array.isArray(entry.resolution_steps) && entry.resolution_steps.length > 0 && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h4 className="font-medium text-sm mb-2">{t('ak.resolutionStepsLabel')}</h4>
                      <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                        {entry.resolution_steps.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            
            {filteredEntries.length === 0 && (
              <div className="text-center py-12">
                <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('ak.noEntriesFound')}</h3>
                <p className="text-gray-500">{t('ak.noEntriesDesc')}</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Create Entry Tab */}
        <TabsContent value="create" className="space-y-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Plus className="h-5 w-5 text-green-600" />
                <span>{t('ak.createKbEntry')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('ak.entryTitleRequired')}</label>
                  <Input
                    placeholder={t('ak.enterEntryTitle')}
                    value={newEntry.title}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('ak.category')}</label>
                  <Select 
                    value={newEntry.category} 
                    onValueChange={(value) => setNewEntry(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category} value={category}>
                          {t('ak.cat.' + category)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">{t('ak.contentRequired')}</label>
                <Textarea
                  placeholder={t('ak.enterKbContent')}
                  value={newEntry.content}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, content: e.target.value }))}
                  rows={6}
                />
              </div>

              <div>
                <label className="text-sm font-medium">{t('ak.tagsCommaSeparated')}</label>
                <Input
                  placeholder={t('ak.tagsExamplePlaceholder')}
                  value={newEntry.tags}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, tags: e.target.value }))}
                />
              </div>

              <div>
                <label className="text-sm font-medium">{t('ak.resolutionStepsOnePerLine')}</label>
                <Textarea
                  placeholder={t('ak.resolutionStepsPlaceholder')}
                  value={newEntry.resolution_steps}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, resolution_steps: e.target.value }))}
                  rows={4}
                />
              </div>

              <Button 
                onClick={handleSaveEntry}
                className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {t('ak.saveEntry')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                <span>{t('ak.kbAnalytics')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t('ak.performanceOverview')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{t('ak.overallSuccessRate')}</span>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-green-600">
                          {entries.length > 0 ? (entries.reduce((sum, e) => sum + e.success_rate, 0) / entries.length).toFixed(1) : 0}%
                        </span>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      </div>
                    </div>
                    <Progress value={entries.length > 0 ? (entries.reduce((sum, e) => sum + e.success_rate, 0) / entries.length) : 0} className="h-3" />
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{t('ak.knowledgeCoverage')}</span>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-blue-600">{new Set(entries.map(e => e.category)).size}</span>
                        <Target className="h-4 w-4 text-blue-500" />
                      </div>
                    </div>
                    <Progress value={(new Set(entries.map(e => e.category)).size / categories.length) * 100} className="h-3" />
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{t('ak.usageFrequency')}</span>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-purple-600">{entries.reduce((sum, e) => sum + e.usage_count, 0)}</span>
                        <TrendingUp className="h-4 w-4 text-purple-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t('ak.topPerformingEntries')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {entries
                        .sort((a, b) => b.success_rate - a.success_rate)
                        .slice(0, 5)
                        .map((entry, index) => (
                          <div key={entry.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center space-x-2">
                              <Badge variant="outline" className="text-xs">{index + 1}</Badge>
                              <span className="text-sm font-medium truncate">{entry.title}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Star className="h-3 w-3 text-yellow-500 fill-current" />
                              <span className="text-xs font-semibold">{entry.success_rate}%</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="text-center">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      onClick={handleViewAnalytics}
                      className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      {t('ak.viewDetailedAnalytics')}
                    </Button>
                  </DialogTrigger>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Entry Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('ak.editKbEntry')}</DialogTitle>
            <DialogDescription>
              {t('ak.editKbEntryDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('ak.entryTitleRequired')}</label>
                <Input
                  placeholder={t('ak.enterEntryTitle')}
                  value={editEntry.title}
                  onChange={(e) => setEditEntry(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('ak.category')}</label>
                <Select 
                  value={editEntry.category} 
                  onValueChange={(value) => setEditEntry(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(category => (
                      <SelectItem key={category} value={category}>
                        {t('ak.cat.' + category)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">{t('ak.contentRequired')}</label>
              <Textarea
                placeholder={t('ak.enterKbContent')}
                value={editEntry.content}
                onChange={(e) => setEditEntry(prev => ({ ...prev, content: e.target.value }))}
                rows={6}
              />
            </div>

            <div>
              <label className="text-sm font-medium">{t('ak.tagsCommaSeparated')}</label>
              <Input
                placeholder={t('ak.tagsExamplePlaceholder')}
                value={editEntry.tags}
                onChange={(e) => setEditEntry(prev => ({ ...prev, tags: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium">{t('ak.resolutionStepsOnePerLine')}</label>
              <Textarea
                placeholder={t('ak.resolutionStepsPlaceholder')}
                value={editEntry.resolution_steps}
                onChange={(e) => setEditEntry(prev => ({ ...prev, resolution_steps: e.target.value }))}
                rows={4}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleEditEntry}>
                <Save className="h-4 w-4 mr-2" />
                {t('ak.updateEntry')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('ak.areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('ak.deleteConfirmPrefix')}
              <strong> "{selectedEntry?.title}"</strong> {t('ak.deleteConfirmSuffix')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteEntry}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('ak.deleteEntry')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Detailed Analytics Dialog */}
      <Dialog open={analyticsDialogOpen} onOpenChange={setAnalyticsDialogOpen}>
        <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Brain className="h-5 w-5 text-blue-600" />
              <span>{t('ak.detailedKbAnalytics')}</span>
            </DialogTitle>
            <DialogDescription>
              {t('ak.detailedKbAnalyticsDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Analytics Overview Cards */}
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{entries.length}</div>
                  <div className="text-sm text-gray-600">{t('ak.totalEntries')}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {entries.length > 0 ? (entries.reduce((sum, e) => sum + e.success_rate, 0) / entries.length).toFixed(1) : 0}%
                  </div>
                  <div className="text-sm text-gray-600">{t('ak.avgSuccessRate')}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">{entries.reduce((sum, e) => sum + e.usage_count, 0)}</div>
                  <div className="text-sm text-gray-600">{t('ak.totalUsage')}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">{new Set(entries.map(e => e.category)).size}</div>
                  <div className="text-sm text-gray-600">{t('ak.categories')}</div>
                </CardContent>
              </Card>
            </div>

            {/* Category Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('ak.categoryPerformanceAnalysis')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {categories.map(category => {
                    const categoryEntries = entries.filter(e => e.category === category);
                    const avgSuccess = categoryEntries.length > 0 
                      ? (categoryEntries.reduce((sum, e) => sum + e.success_rate, 0) / categoryEntries.length) 
                      : 0;
                    const totalUsage = categoryEntries.reduce((sum, e) => sum + e.usage_count, 0);
                    
                    return (
                      <div key={category} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <div className="flex items-center space-x-3">
                          <Badge variant="outline">{t('ak.cat.' + category)}</Badge>
                          <span className="text-sm text-gray-600">{t('ak.entriesLower', { count: categoryEntries.length })}</span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className="text-sm font-semibold text-green-600">{avgSuccess.toFixed(1)}%</div>
                            <div className="text-xs text-gray-500">{t('ak.successRate')}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-purple-600">{totalUsage}</div>
                            <div className="text-xs text-gray-500">{t('ak.usage')}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Performance Insights */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('ak.aiPerformanceInsights')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-2 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {t('ak.highPerformingContent')}
                    </h4>
                    <p className="text-sm text-green-700 mb-3">
                      {t('ak.entriesAbove90', { count: entries.filter(e => e.success_rate > 90).length })}
                    </p>
                    <div className="space-y-1">
                      {entries
                        .filter(e => e.success_rate > 90)
                        .slice(0, 3)
                        .map(entry => (
                          <div key={entry.id} className="text-xs text-green-600">
                            - {entry.title} ({entry.success_rate}%)
                          </div>
                        ))}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-medium text-yellow-800 mb-2 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      {t('ak.optimizationOpportunities')}
                    </h4>
                    <p className="text-sm text-yellow-700 mb-3">
                      {t('ak.entriesBenefitOptimization', { count: entries.filter(e => e.success_rate < 80).length })}
                    </p>
                    <div className="space-y-1">
                      {entries
                        .filter(e => e.success_rate < 80)
                        .slice(0, 3)
                        .map(entry => (
                          <div key={entry.id} className="text-xs text-yellow-600">
                            - {entry.title} ({entry.success_rate}%)
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Usage Trends */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('ak.usageTrendsRecommendations')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                    <span className="text-sm font-medium text-blue-900">{t('ak.mostUsedEntry')}</span>
                    <div className="text-sm text-blue-700">
                      {entries.length > 0 && entries.reduce((prev, current) => (prev.usage_count > current.usage_count) ? prev : current).title}
                      ({entries.length > 0 && Math.max(...entries.map(e => e.usage_count))} {t('ak.uses')})
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded">
                    <span className="text-sm font-medium text-purple-900">{t('ak.bestPerformingEntry')}</span>
                    <div className="text-sm text-purple-700">
                      {entries.length > 0 && entries.reduce((prev, current) => (prev.success_rate > current.success_rate) ? prev : current).title}
                      ({entries.length > 0 && Math.max(...entries.map(e => e.success_rate))}% {t('ak.success')})
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded">
                    <h5 className="font-medium text-gray-900 mb-2">{t('ak.aiRecommendations')}</h5>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>- {t('ak.recExpandCategories')}</li>
                      <li>- {t('ak.recReviewLowSuccess')}</li>
                      <li>- {t('ak.recAddResolutionSteps')}</li>
                      <li>- {t('ak.recMonitorFeedback')}</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeBase;